import {
    Component,
    DestroyRef,
    inject,
    input,
    type OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { type Observable } from 'rxjs';
import type {
    OcrFieldSuggestion,
    OcrSuggestions
} from '../../models/ocr/ocr-suggestion';
import { EmbarkSpinnerComponent } from '../embark-spinner/embark-spinner.component';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { InputText } from 'primeng/inputtext';
import { type OcrResponse } from '../../models/ocr/ocr-response';

@Component({
    selector: 'app-ocr-suggestions',
    templateUrl: './ocr-suggestions.component.html',
    styleUrl: './ocr-suggestions.component.sass',
    imports: [
        DecimalPipe,
        NgTemplateOutlet,
        FormsModule,
        EmbarkSpinnerComponent,
        Button,
        Checkbox,
        InputText
    ]
})
export class OcrSuggestionsComponent implements OnInit {
    public currentValues = input<Record<string, string>>();
    public fetchFn =
        input.required<(refresh?: boolean) => Observable<OcrResponse>>();
    public buttonText = input<string>('Submit Changes');
    public fieldLabels = input<Record<string, string>>({});
    public outboundOnlyFields = input<string[]>(['serviceDate']);
    public canSubmit = input<boolean>(false);
    public updateOnlyButtonText = input<string>('Update Invoice');
    public submitFn =
        input<
            (
                changes: OcrSuggestions,
                updateOnly: boolean
            ) => Observable<unknown>
        >();

    public suggestionsLoading = false;
    public submitting = false;
    public suggestionsExpanded = false;
    public suggestions: [string, OcrFieldSuggestion][] = [];
    public selectedFields: Record<string, boolean> = {};
    public editedValues: Record<string, string> = {};
    public queriedAt: Date | null = null;

    private readonly destroyRef = inject(DestroyRef);
    private originalOcrValues: Record<string, string> = {};

    private readonly defaultFieldLabels: Record<string, string> = {
        ticketNumber: 'Ticket Number',
        invoiceNumber: 'Invoice Number',
        salesOrderID: 'Sales Order ID',
        name: 'Name',
        date: 'Date',
        description: 'Description',
        fieldApprover: 'Field Approver',
        lineServiceDateFrom: 'Service Date From',
        lineServiceDateTo: 'Service Date To',
        lineDescription: 'Line Description',
        location: 'Location',
        jobLocation: 'Job Location',
        units: 'Units',
        amount: 'Amount',
        po: 'PO',
        purchaseOrder: 'Purchase Order',
        poNumber: 'PO Number',
        afe: 'AFE',
        afeLoe: 'AFE/LOE',
        costCenter: 'Cost Center',
        area: 'Area',
        cc: 'Cost Code',
        woNumber: 'WO Number',
        platform: 'Platform',
        glCode: 'GL Code',
        category: 'Category',
        serviceDate: 'Service Date'
    };

    public get hasSelections(): boolean {
        return Object.values(this.selectedFields).some(Boolean);
    }

    public ngOnInit(): void {
        this.fetch();
    }

    private fetch(refresh = false): void {
        this.suggestionsLoading = true;
        this.fetchFn()(refresh).subscribe({
            next: (response) => {
                this.suggestions = Object.entries(response.suggestions);
                this.queriedAt = response.queriedAt;
                this.originalOcrValues = {};
                this.editedValues = {};
                for (const [key, suggestion] of this.suggestions) {
                    const raw = suggestion.value;
                    const isPrimitive =
                        typeof raw === 'string' ||
                        typeof raw === 'number' ||
                        typeof raw === 'boolean';
                    const val = isPrimitive ? String(raw) : '';
                    this.originalOcrValues[key] = val;
                    this.editedValues[key] = val;
                }
                if (this.suggestions.length > 0) {
                    this.suggestionsExpanded = true;
                }
                this.suggestionsLoading = false;
            },
            error: () => {
                this.suggestionsLoading = false;
            }
        });
    }

    public isOutboundOnly(key: string): boolean {
        return this.outboundOnlyFields().includes(key);
    }

    public getFieldLabel(key: string): string {
        return (
            this.fieldLabels()[key] ??
            this.defaultFieldLabels[key] ??
            key.replace(/([A-Z])/g, ' $1').trim()
        );
    }

    public getInvoiceFieldValue(key: string): string {
        const val = this.currentValues()?.[key];
        return val !== undefined && val !== '' ? val : '—';
    }

    public confidenceColor(confidence: number): string {
        if (confidence > 0.9) return '#2d8c2d';
        if (confidence > 0.8) return '#c9a020';
        return '#c93030';
    }

    public confidenceOffset(confidence: number): number {
        const circumference = 2 * Math.PI * 18;
        return circumference * (1 - confidence);
    }

    public toggleExpanded(): void {
        this.suggestionsExpanded = !this.suggestionsExpanded;
    }

    public toggleField(key: string, checked: boolean): void {
        this.selectedFields = { ...this.selectedFields, [key]: checked };
    }

    public clearChanges(): void {
        this.selectedFields = {};
        this.editedValues = { ...this.originalOcrValues };
    }

    public submitChanges(): void {
        const fn = this.submitFn();
        if (!fn) return;

        const suggestionMap = new Map(this.suggestions);
        const changes = Object.entries(this.selectedFields)
            .filter(([, selected]) => selected)
            .reduce<OcrSuggestions>(
                (acc, [key]) => ({
                    ...acc,
                    [key]: {
                        value: this.editedValues[key] ?? '',
                        confidence: suggestionMap.get(key)?.confidence ?? 0
                    }
                }),
                {}
            );

        this.submitting = true;
        fn(changes, !this.canSubmit())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.submitting = false;
                    this.clearChanges();
                    this.suggestionsExpanded = false;
                    this.refreshSuggestions();
                },
                error: () => {
                    this.submitting = false;
                }
            });
    }

    public getSuggestionDate(): string {
        if (!this.queriedAt) return '';
        const date = new Date(this.queriedAt);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        });
    }

    public refreshSuggestions(): void {
        this.fetch(true);
    }
}
